import { useCallback, useEffect, useRef, useState } from 'react'
import { APP_VERSION } from '@/constants/appVersion'
import {
  fetchDeployedVersion,
  isNewerVersionAvailable,
  reloadApp,
} from '@/lib/appUpdate'
import { useUserLogs } from '@/hooks/useUserLogs'

const CHECK_INTERVAL_MS = 5 * 60 * 1000
const INITIAL_CHECK_DELAY_MS = 3000

export function useAppUpdate() {
  const { logAction } = useUserLogs()
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [deployedVersion, setDeployedVersion] = useState<string | null>(null)
  const dismissedVersionRef = useRef<string | null>(null)

  const checkForUpdate = useCallback(async () => {
    const deployed = await fetchDeployedVersion()
    if (!deployed) return

    if (dismissedVersionRef.current === deployed) return

    if (isNewerVersionAvailable(deployed)) {
      setDeployedVersion(deployed)
      setUpdateAvailable(true)
    }
  }, [])

  const dismiss = useCallback(async () => {
    if (deployedVersion) {
      await logAction(
        'other',
        'other',
        null,
        `Clicou em Depois no aviso de nova versão do app (permanece em v${APP_VERSION}, disponível v${deployedVersion})`,
        {
          current_version: APP_VERSION,
          deployed_version: deployedVersion,
          additional_info: 'app_update_dismiss',
        }
      )
      dismissedVersionRef.current = deployedVersion
    }
    setUpdateAvailable(false)
  }, [deployedVersion, logAction])

  const reload = useCallback(async () => {
    if (deployedVersion) {
      await logAction(
        'other',
        'other',
        null,
        `Clicou em Atualizar agora no aviso de nova versão do app (v${APP_VERSION} → v${deployedVersion})`,
        {
          current_version: APP_VERSION,
          deployed_version: deployedVersion,
          additional_info: 'app_update_reload',
        }
      )
    }
    reloadApp()
  }, [deployedVersion, logAction])

  useEffect(() => {
    if (import.meta.env.DEV) return

    const initialTimer = window.setTimeout(checkForUpdate, INITIAL_CHECK_DELAY_MS)
    const intervalId = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS)
    const onFocus = () => {
      void checkForUpdate()
    }

    window.addEventListener('focus', onFocus)

    return () => {
      window.clearTimeout(initialTimer)
      window.clearInterval(intervalId)
      window.removeEventListener('focus', onFocus)
    }
  }, [checkForUpdate])

  return {
    updateAvailable,
    deployedVersion,
    dismiss,
    reload,
  }
}
